import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { AlertCircle, AlertTriangle, Upload, CheckCheck } from 'lucide-react';
import { transactionsAPI, accountsAPI } from '../lib/api';
import { formatCurrency, getAmountClass } from '../lib/utils';
import { useOrg } from '../contexts/OrgContext';
import { usePullToRefresh } from '../hooks/usePullToRefresh';
import DataAssistant from '../components/ai/DataAssistant';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { toast } from 'sonner';

import { TransactionCard } from '../components/transactions/TransactionCard';
import { EditTransactionDialog } from '../components/transactions/EditTransactionDialog';
import {
  PostConfirmDialog,
  ApproveAllDialog,
  ApproveAndPostDialog,
  ReverseDialog,
  DeleteDialog,
  NewAccountDialog,
} from '../components/transactions/ConfirmationDialogs';

const accountTypes = ['asset', 'liability', 'equity', 'revenue', 'expense'];
const currencies = ['CHF', 'EUR', 'USD'];

export default function TransactionsPage() {
  const { isPersonal } = useOrg();
  const [transactions, setTransactions] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending');
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [editData, setEditData] = useState({});
  const [processing, setProcessing] = useState({});
  const [deleteTransaction, setDeleteTransaction] = useState(null);
  const [postingAll, setPostingAll] = useState(false);
  const [showPostConfirm, setShowPostConfirm] = useState(false);
  const [reverseTransaction, setReverseTransaction] = useState(null);
  const [approvingAll, setApprovingAll] = useState(false);
  const [showApproveAllConfirm, setShowApproveAllConfirm] = useState(false);
  const [showApproveAndPostConfirm, setShowApproveAndPostConfirm] = useState(false);
  const [reBlockMessage, setReBlockMessage] = useState(null);
  
  // Pagination state
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [tabCounts, setTabCounts] = useState({ pending: 0, approved: 0, posted: 0, rejected: 0 });
  const PAGE_SIZE = 50;
  
  // Account selector state
  const [accountSearchOpen, setAccountSearchOpen] = useState(false);
  const [accountSearch, setAccountSearch] = useState('');
  
  // New account dialog state
  const [showNewAccountDialog, setShowNewAccountDialog] = useState(false);
  const [newAccountData, setNewAccountData] = useState({
    code: '', name: '', account_type: 'expense', description: '', currency: 'CHF'
  });

  const accountsRef = useRef(accounts);
  accountsRef.current = accounts;

  const loadData = useCallback(async () => {
    try {
      const currentAccounts = accountsRef.current;
      const [txnRes, accRes, countsRes] = await Promise.all([
        transactionsAPI.getAll({ status: activeTab, page, page_size: PAGE_SIZE }),
        currentAccounts.length ? Promise.resolve({ data: currentAccounts }) : accountsAPI.getAll(),
        transactionsAPI.getCounts(),
      ]);
      const data = txnRes.data;
      setTransactions(data.items || data);
      setTotalPages(data.pages || 1);
      setTotalItems(data.total || 0);
      if (!currentAccounts.length) setAccounts(accRes.data);
      setTabCounts(countsRes.data);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load transactions');
    } finally {
      setLoading(false);
    }
  }, [activeTab, page]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleTabChange = (tab) => { setActiveTab(tab); setPage(1); };

  const { containerRef: pullRef, pullIndicator } = usePullToRefresh(loadData);

  const pendingCount = tabCounts.pending;
  const pendingWithAccountCount = transactions.filter(t => t.suggested_account_id).length;
  const pendingWithoutAccountCount = activeTab === 'pending' ? totalItems - pendingWithAccountCount : 0;
  const approvedCount = tabCounts.approved;
  const postedCount = tabCounts.posted;

  // Suggested accounts based on transaction type
  const filteredAccounts = useMemo(() => {
    if (!editingTransaction) return accounts;
    const suggestedTypes = editingTransaction.amount < 0 ? ['expense'] : ['revenue'];
    return accounts.filter(a => {
      if (!suggestedTypes.includes(a.account_type)) return false;
      if (accountSearch) {
        const search = accountSearch.toLowerCase();
        return a.code.toLowerCase().includes(search) || a.name.toLowerCase().includes(search) || a.description?.toLowerCase().includes(search);
      }
      return true;
    });
  }, [accounts, editingTransaction, accountSearch]);

  // All accounts for search (not filtered by type)
  const allFilteredAccounts = useMemo(() => {
    if (!accountSearch) return accounts;
    const search = accountSearch.toLowerCase();
    return accounts.filter(a =>
      a.code.toLowerCase().includes(search) || a.name.toLowerCase().includes(search) || a.description?.toLowerCase().includes(search)
    );
  }, [accounts, accountSearch]);

  const getSelectedAccountName = () => {
    const account = accounts.find(a => a.id === editData.suggested_account_id);
    return account ? `${account.code} - ${account.name}` : 'Select account...';
  };

  // --- Action Handlers ---
  const handleApprove = async (txnId) => {
    setProcessing(prev => ({ ...prev, [txnId]: 'approve' }));
    try {
      await transactionsAPI.approve(txnId);
      toast.success('Transaction approved');
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve transaction');
    } finally {
      setProcessing(prev => ({ ...prev, [txnId]: null }));
    }
  };

  const handleReject = async (txnId) => {
    setProcessing(prev => ({ ...prev, [txnId]: 'reject' }));
    try {
      await transactionsAPI.reject(txnId);
      toast.success('Transaction rejected');
      await loadData();
    } catch (error) {
      toast.error('Failed to reject transaction');
    } finally {
      setProcessing(prev => ({ ...prev, [txnId]: null }));
    }
  };

  const handleDelete = async () => {
    if (!deleteTransaction) return;
    try {
      await transactionsAPI.delete(deleteTransaction.id);
      toast.success('Transaction deleted');
      setDeleteTransaction(null);
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete transaction');
    }
  };

  const handleApproveAndPost = async () => {
    setPostingAll(true);
    setReBlockMessage(null);
    try {
      await transactionsAPI.approveAll();
      const res = await transactionsAPI.postAll();
      toast.success(res.data.message);
      setShowApproveAndPostConfirm(false);
      await loadData();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to post transactions';
      setShowApproveAndPostConfirm(false);
      if (error.response?.status === 422) { setReBlockMessage(message); } else { toast.error(message); }
    } finally {
      setPostingAll(false);
    }
  };

  const handlePostAllToGL = async () => {
    setPostingAll(true);
    setReBlockMessage(null);
    try {
      const res = await transactionsAPI.postAll();
      toast.success(res.data.message);
      setShowPostConfirm(false);
      await loadData();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to post transactions';
      setShowPostConfirm(false);
      if (error.response?.status === 422) { setReBlockMessage(message); } else { toast.error(message); }
    } finally {
      setPostingAll(false);
    }
  };

  const handleReversePosting = async () => {
    if (!reverseTransaction) return;
    try {
      await transactionsAPI.reverse(reverseTransaction.id);
      toast.success('Transaction posting reversed');
      setReverseTransaction(null);
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reverse posting');
    }
  };

  const handleApproveAll = async () => {
    setApprovingAll(true);
    try {
      const res = await transactionsAPI.approveAll();
      const { approved_count, skipped_count } = res.data;
      toast.success(`${approved_count} transaction${approved_count !== 1 ? 's' : ''} approved${skipped_count > 0 ? ` (${skipped_count} skipped — no account assigned)` : ''}`);
      setShowApproveAllConfirm(false);
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve transactions');
    } finally {
      setApprovingAll(false);
    }
  };

  const handleUnapprove = async (txnId) => {
    setProcessing(prev => ({ ...prev, [txnId]: 'unapprove' }));
    try {
      await transactionsAPI.unapprove(txnId);
      toast.success('Transaction sent back to pending');
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send back to pending');
    } finally {
      setProcessing(prev => ({ ...prev, [txnId]: null }));
    }
  };

  const openEditDialog = (txn) => {
    setEditingTransaction(txn);
    setEditData({
      description: txn.description || '', amount: txn.amount, date: txn.date || '',
      suggested_account_id: txn.suggested_account_id || '', notes: txn.notes || '',
      tax_rate: txn.tax_rate ?? null, is_tax_exempt: txn.is_tax_exempt || false,
    });
    setAccountSearch('');
  };

  const handleSaveEdit = async () => {
    if (!editingTransaction) return;
    try {
      const payload = { ...editData };
      if (payload.description === editingTransaction.description) delete payload.description;
      if (payload.amount === editingTransaction.amount) delete payload.amount;
      if (payload.date === editingTransaction.date) delete payload.date;
      const currentAmount = payload.amount !== undefined ? payload.amount : editingTransaction.amount;
      if (payload.tax_rate !== null && payload.tax_rate !== undefined && !payload.is_tax_exempt) {
        const gross = Math.abs(currentAmount);
        const net = parseFloat((gross / (1 + payload.tax_rate / 100)).toFixed(2));
        const tax = parseFloat((gross - net).toFixed(2));
        const sign = currentAmount >= 0 ? 1 : -1;
        payload.net_amount = net * sign;
        payload.tax_amount = tax * sign;
      }
      await transactionsAPI.update(editingTransaction.id, payload);
      toast.success('Transaction updated');
      setEditingTransaction(null);
      await loadData();
    } catch (error) {
      toast.error('Failed to update transaction');
    }
  };

  const handleCreateAccount = async () => {
    try {
      const res = await accountsAPI.create(newAccountData);
      toast.success('Account created');
      setAccounts(prev => [...prev, res.data]);
      setEditData(prev => ({ ...prev, suggested_account_id: res.data.id }));
      setShowNewAccountDialog(false);
      setNewAccountData({ code: '', name: '', account_type: 'expense', description: '', currency: 'CHF' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create account');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#D94E18] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div ref={pullRef} className="space-y-6 animate-fade-in">
      {pullIndicator}

      <DataAssistant transactions={transactions} onHighlight={(action) => {
        if (action === 'highlight_duplicates' || action === 'highlight_vat_issues' || action === 'highlight_uncertain') setActiveTab('pending');
      }} />

      {/* Posting Blocked Banner */}
      {reBlockMessage && (
        <Card className="border-red-300 bg-red-50" data-testid="re-block-alert">
          <CardContent className="py-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-red-800 text-sm">Posting Blocked — Data Integrity Issue</p>
                <p className="text-sm text-red-700 mt-1">{reBlockMessage}</p>
                <button onClick={() => setReBlockMessage(null)} className="text-xs text-red-500 underline mt-2">Dismiss</button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Approved Ready-to-Post Banner */}
      {approvedCount > 0 && (
        <Card className="border-[#D94E18] bg-orange-50">
          <CardContent className="py-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <p className="font-medium text-zinc-900">
                  {approvedCount} transaction{approvedCount > 1 ? 's' : ''} ready to post
                </p>
                <p className="text-sm text-zinc-500">Review complete. Post all approved transactions to the General Ledger.</p>
              </div>
              <Button onClick={() => setShowPostConfirm(true)} className="bg-[#D94E18] hover:bg-[#C2410C] w-full sm:w-auto" data-testid="post-all-btn">
                <Upload className="w-4 h-4 mr-2" />Post All to GL
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList className="bg-zinc-100 w-full overflow-x-auto flex">
          <TabsTrigger value="pending" className="data-[state=active]:bg-white flex-1 min-w-0" data-testid="tab-pending">
            <span className="truncate">Pending</span>
            {pendingCount > 0 && <Badge variant="secondary" className="ml-1.5 bg-amber-100 text-amber-800 flex-shrink-0">{pendingCount}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="approved" className="data-[state=active]:bg-white flex-1 min-w-0" data-testid="tab-approved">
            <span className="truncate">Approved</span>
            {approvedCount > 0 && <Badge variant="secondary" className="ml-1.5 bg-blue-100 text-blue-800 flex-shrink-0">{approvedCount}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="posted" className="data-[state=active]:bg-white flex-1 min-w-0" data-testid="tab-posted">
            <span className="truncate">Posted</span>
            {postedCount > 0 && <Badge variant="secondary" className="ml-1.5 bg-emerald-100 text-emerald-800 flex-shrink-0">{postedCount}</Badge>}
          </TabsTrigger>
          <TabsTrigger value="rejected" className="data-[state=active]:bg-white flex-1 min-w-0" data-testid="tab-rejected">
            <span className="truncate">Rejected</span>
          </TabsTrigger>
        </TabsList>

        {/* Pending Tab Banners */}
        {activeTab === 'pending' && pendingCount > 0 && pendingWithAccountCount === 0 && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg" data-testid="all-unclassified-banner">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-red-800">
                  {pendingCount} transaction{pendingCount !== 1 ? 's' : ''} need{pendingCount === 1 ? 's' : ''} classification
                </p>
                <p className="text-xs text-red-600 mt-0.5">AI couldn't classify these automatically. Tap the edit button on each to assign an account before posting.</p>
              </div>
            </div>
          </div>
        )}
        {activeTab === 'pending' && pendingWithAccountCount > 0 && (
          <div className="mt-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
            <div>
              <p className="text-sm font-medium text-zinc-900">
                {pendingWithAccountCount} of {pendingCount} transaction{pendingCount !== 1 ? 's' : ''} ready to process
              </p>
              {pendingWithoutAccountCount > 0 && (
                <p className="text-xs text-red-600 font-medium">
                  <AlertTriangle className="w-3 h-3 inline mr-1" />
                  {pendingWithoutAccountCount} need{pendingWithoutAccountCount === 1 ? 's' : ''} manual classification — tap edit to assign an account
                </p>
              )}
            </div>
            <div className="flex gap-2 w-full sm:w-auto">
              <Button size="sm" onClick={() => setShowApproveAndPostConfirm(true)} className="bg-[#D94E18] hover:bg-[#C2410C] flex-1 sm:flex-initial" data-testid="approve-and-post-btn">
                <Upload className="w-4 h-4 mr-2" />Approve & Post to GL
              </Button>
              <Button size="sm" variant="outline" onClick={() => setShowApproveAllConfirm(true)} className="border-emerald-300 text-emerald-700 hover:bg-emerald-100 flex-1 sm:flex-initial" data-testid="approve-all-btn">
                <CheckCheck className="w-4 h-4 mr-2" />Approve Only
              </Button>
            </div>
          </div>
        )}

        <TabsContent value={activeTab} className="mt-4">
          {transactions.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <AlertCircle className="w-12 h-12 text-zinc-300 mx-auto mb-4" />
                <p className="text-zinc-500">No {activeTab} transactions</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {transactions.map((txn) => (
                <TransactionCard
                  key={txn.id}
                  txn={txn}
                  isPersonal={isPersonal}
                  processing={processing}
                  onEdit={openEditDialog}
                  onDelete={setDeleteTransaction}
                  onReject={handleReject}
                  onApprove={handleApprove}
                  onUnapprove={handleUnapprove}
                  onReverse={setReverseTransaction}
                />
              ))}
            </div>
          )}
        </TabsContent>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-4 px-1" data-testid="transactions-pagination">
            <p className="text-sm text-zinc-500">Page {page} of {totalPages} ({totalItems} total)</p>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))} data-testid="txn-prev-page">Previous</Button>
              <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))} data-testid="txn-next-page">Next</Button>
            </div>
          </div>
        )}
      </Tabs>

      {/* Dialogs */}
      <PostConfirmDialog open={showPostConfirm} onOpenChange={setShowPostConfirm} approvedCount={approvedCount} onConfirm={handlePostAllToGL} posting={postingAll} />
      <ApproveAllDialog open={showApproveAllConfirm} onOpenChange={setShowApproveAllConfirm} withAccount={pendingWithAccountCount} withoutAccount={pendingWithoutAccountCount} onConfirm={handleApproveAll} approving={approvingAll} />
      <ApproveAndPostDialog open={showApproveAndPostConfirm} onOpenChange={setShowApproveAndPostConfirm} withoutAccount={pendingWithoutAccountCount} onConfirm={handleApproveAndPost} posting={postingAll} />
      <ReverseDialog transaction={reverseTransaction} onOpenChange={() => setReverseTransaction(null)} onConfirm={handleReversePosting} />
      <DeleteDialog transaction={deleteTransaction} onOpenChange={() => setDeleteTransaction(null)} onConfirm={handleDelete} />

      <EditTransactionDialog
        editingTransaction={editingTransaction}
        editData={editData}
        setEditData={setEditData}
        isPersonal={isPersonal}
        accountSearchOpen={accountSearchOpen}
        setAccountSearchOpen={setAccountSearchOpen}
        accountSearch={accountSearch}
        setAccountSearch={setAccountSearch}
        filteredAccounts={filteredAccounts}
        allFilteredAccounts={allFilteredAccounts}
        getSelectedAccountName={getSelectedAccountName}
        onSave={handleSaveEdit}
        onClose={() => setEditingTransaction(null)}
        onNewAccount={() => setShowNewAccountDialog(true)}
      />

      <NewAccountDialog
        open={showNewAccountDialog}
        onOpenChange={setShowNewAccountDialog}
        data={newAccountData}
        setData={setNewAccountData}
        currencies={currencies}
        accountTypes={accountTypes}
        onCreate={handleCreateAccount}
      />
    </div>
  );
}
